import { useState, useEffect, useRef } from "react";
import { useGetWhatsAppMessagesQuery, useSendWhatsAppReplyMutation } from "../slices/whatsAppApiSlice";
import Loader from "./Loader";
import Message from "./Message";
import { Row, Col, Form, ListGroup, InputGroup, Button, Card } from "react-bootstrap";

const WhatsAppChat = () => {
  const businessPhoneNumber = process.env.REACT_APP_BUSINESS_PHONE_NUMBER;
  
  const { data: items, isLoading, error, refetch } = useGetWhatsAppMessagesQuery();
  const [sendWhatsAppReply] = useSendWhatsAppReplyMutation();

  const [selectedChat, setSelectedChat] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const messagesEndRef = useRef(null);

  const handleSend = async () => {
    if (!selectedChat || !replyText.trim()) return;
    await sendWhatsAppReply({ to: selectedChat.from, message: replyText });
    setReplyText("");
    refetch();
  };
  
  const uniqueChats = items
    ? [...new Set(items
        .map(msg => msg.from)
        .filter(num => num !== businessPhoneNumber) // exclude business phone number
      )]
    : [];


  const filteredChats = uniqueChats.filter((phone) =>
    phone.toLowerCase().includes(phoneFilter.toLowerCase())
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [items, selectedChat]);

  return (
    <Row className="p-3" style={{ height: "80vh" }}>
      {/* LEFT: Chats list */}
      <Col md={3} className="border-end">
        <h5 className="mb-3">Chats</h5>

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Search or enter phone..."
            value={phoneFilter}
            onChange={(e) => setPhoneFilter(e.target.value)}
          />
          <Button
            variant="success"
            onClick={() => {
              if (phoneFilter.trim()) {
                setSelectedChat({ from: phoneFilter.trim() });
              }
            }}
          >
            Open
          </Button>
        </InputGroup>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error}</Message>
        ) : (
          <div
            style={{
              maxHeight: "160px", // ~3 list items
              overflowY: "auto",
              marginBottom: "15px" // ensures space in mobile layout
            }}
          >
            <ListGroup>
              {filteredChats.map((phone) => (
                <ListGroup.Item
                  key={phone}
                  action
                  active={selectedChat?.from === phone}
                  onClick={() => setSelectedChat({ from: phone })}
                >
                  {phone}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Col>

      {/* RIGHT: Chat window */}
      <Col md={9} className="d-flex flex-column">
        {selectedChat ? (
          <>
            <Card className="flex-grow-1 mb-2">
              <Card.Body
                style={{
                  overflowY: "auto",
                  height: "65vh",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {items
                  ?.filter(
                    (m) =>
                      m.from === selectedChat.from ||
                      m.to === selectedChat.from
                  )
                  .map((msg) => (
                    <div
                      key={msg._id}
                      className={`d-flex mb-2 ${
                        msg.from === selectedChat.from
                          ? "justify-content-start"
                          : "justify-content-end"
                      }`}
                    >
                      <div
                        className={`p-2 rounded ${
                          msg.from === selectedChat.from
                            ? "bg-light"
                            : "bg-primary text-white"
                        }`}
                        style={{ maxWidth: "75%" }}
                      >
                        {/* TEXT MESSAGE */}
                        {msg.message && (
                          <div>{msg.message}</div>
                        )}

                        {/* IMAGE MESSAGE */}
                        {msg.mediaType === "image" && (
                          <img
                            src={msg.mediaUrl}
                            alt="Pictorial Message"
                            style={{ maxWidth: "200px", borderRadius: "8px", marginTop: "5px" }}
                          />
                        )}

                        {/* VIDEO MESSAGE */}
                        {msg.mediaType === "video" && (
                          <video
                            src={msg.mediaUrl}
                            controls
                            style={{ maxWidth: "200px", borderRadius: "8px", marginTop: "5px" }}
                          />
                        )}

                        {/* DOCUMENT MESSAGE */}
                        {msg.mediaType === "document" && (
                          <a
                            href={msg.mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "block", marginTop: "5px" }}
                          >
                            📄 {msg.message || "Open Document"}
                          </a>
                        )}

                        {/* AUDIO MESSAGE */}
                        {msg.mediaType === "audio" && (
                          <audio controls style={{ marginTop: "5px" }}>
                            <source src={msg.mediaUrl} type={msg.mimeType} />
                          </audio>
                        )}

                        {/* STICKER */}
                        {msg.mediaType === "sticker" && (
                          <img
                            src={msg.mediaUrl}
                            alt="sticker"
                            style={{ maxWidth: "100px", marginTop: "5px" }}
                          />
                        )}
                        <div className="small text-muted mt-1">
                          {new Date(msg.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                <div ref={messagesEndRef} />
              </Card.Body>
            </Card>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              <InputGroup>
                <Form.Control
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a message..."
                />
                <Button type="submit" variant="primary">
                  Send
                </Button>
              </InputGroup>
            </Form>
          </>
        ) : (
          <div className="text-center my-auto">
            <p>Select a chat or enter a phone number</p>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default WhatsAppChat;